<template>
  <v-card :class="{'price-change-card': hasPriceChanges}">
    <div :class="cardCss">
      <v-badge v-if="hasProductBoost" bordered icon="mdi-rocket-launch" left overlap color="red"
               class="rocket-boost"/>
      <div
        class="d-flex flex-grow-1 | flex-md-row flex-row-reverse | justify-md-start justify-space-between | align-center mr-md-5 | checkbox">
        <v-checkbox dense class="align-center justify-center ma-0 pa-0"
                    :input-value="generalConfirmedValue"
                    hide-details :disabled="isConfirmationDisabled" @click="onConfirmAll"
                    :indeterminate="isConfirmationIndeterminate"/>
        <div class="ml-md-0 ml-1 flex-grow-1 d-flex flex-row align-center">
          <v-btn v-if="hasSubordinateRecommendations" icon @click="onCollapse"
                 class="mr-1 collapse-button">
            <v-icon large color="#000000">{{ collapseIcon }}</v-icon>
          </v-btn>
          <div :class="nameCss" @click="onCollapse">{{ productName }}
          </div>
        </div>
      </div>
      <v-divider v-if="showSubordinateProducts"/>
      <div v-if="showSubordinateProducts" class="d-flex flex-column">
        <div v-for="(rec, index) in recommendations" :key="rec.productRecommendation.guid"
             class="d-flex flex-wrap flex-md-nowrap flex-column flex-md-row justify-md-space-between mt-3 mt-md-0">
          <div
            class="d-flex flex-grow-1 | flex-md-row flex-row-reverse | justify-md-start justify-space-between | align-center mr-md-5 | checkbox">
            <v-checkbox dense class="align-center justify-center ma-0 pa-0"
                        :input-value="rec.productRecommendation.confirmed"
                        hide-details :disabled="isConfirmationDisabled"
                        @click="onConfirm(rec.productRecommendation)"/>
            <div class="font-weight-bold ml-md-0 ml-1 flex-grow-1">{{ rec.saleFormatTitle }}</div>
          </div>
          <product-price-chips :group-info="rec.productRecommendation" :default-width="chipsWidth"
                               :main-product-recommendation="index !== 0 && productRecommendation"
                               :disabled="!rec.productRecommendation.confirmed || showIndexedPrices"
                               :class="chipCssClasses" v-on:prices-valid="onPricesValid"
                               v-on="$listeners"/>
        </div>
      </div>
      <product-price-chips v-if="!hasSubordinateRecommendations" :group-info="productRecommendation"
                           :default-width="chipsWidth" v-on="$listeners"
                           :class="chipCssClasses"
                           :disabled="!productRecommendation.confirmed || showIndexedPrices"
      />
    </div>
    <div v-if="showIndexedPrices" :class="cardCss">
      <div
        class="d-flex flex-grow-1 | flex-md-row flex-row-reverse | justify-md-start justify-space-between | align-center mr-md-5 | checkbox">
        <div class="ml-md-0 ml-1 flex-grow-1 d-flex flex-row align-center">
          <v-btn v-if="hasSubordinateRecommendations" icon @click="onCollapse"
                 class="mr-1 collapse-button">
            <v-icon large color="#000000">{{ collapseIcon }}</v-icon>
          </v-btn>
        </div>
      </div>
      <v-divider v-if="showSubordinateProducts"/>
      <div v-if="showSubordinateProducts" class="d-flex flex-column">
        <div v-for="(rec, index) in recommendations" :key="rec.productRecommendation.guid"
             class="d-flex flex-wrap flex-md-nowrap flex-column flex-md-row justify-md-space-between mt-3 mt-md-0">
          <div
            class="d-flex flex-grow-1 | flex-md-row flex-row-reverse | justify-md-start justify-space-between | align-center mr-md-5 | checkbox">
            <v-checkbox dense class="align-center justify-center ma-0 pa-0"
                        :input-value="rec.productRecommendation.confirmed"
                        hide-details :disabled="isConfirmationDisabled"
                        @click="onConfirm(rec.productRecommendation)"/>
            <div class="font-weight-bold ml-md-0 ml-1 flex-grow-1">{{ rec.saleFormatTitle }}</div>
          </div>
          <indexed-product-price-chips :group-info="getIndexedProductPrices(rec.productRecommendation.guid)"
                                       :default-width="chipsWidth"
                                       :main-product-recommendation="index !== 0 && productRecommendation"
                                       :disabled="false"
                                       :class="chipCssClasses" v-on:prices-valid="onPricesValid"
                                       :product-recommendation="rec.productRecommendation"
                                       v-on="$listeners"/>
        </div>
      </div>
      <indexed-product-price-chips v-if="!hasSubordinateRecommendations"
                                   :group-info="getIndexedProductPrices(productRecommendation.guid)"
                                   :default-width="chipsWidth" v-on="$listeners"
                                   :class="chipCssClasses"
                                   :product-recommendation="productRecommendation"
                                   :disabled="false"
      />
    </div>
    <div style="margin-left: 10px;">
      <v-chip x-small class="mb-1 mr-1">
        <div class="chip-ellipsis">{{ group }}</div>
      </v-chip>
    </div>
  </v-card>
</template>

<script>

import Component from 'vue-class-component';
import Vue from 'vue';
import { Components, MapActions, MapGetters } from '@core/helpers/decorators';
import { Prop, Watch } from 'vue-property-decorator';
import { ProductRecommendation } from '../models';
import ProductPriceChips from './ProductPriceChips.vue';
import IndexedProductPriceChips from './IndexedProductPriceChips.vue';

@Component
@Components({ ProductPriceChips, IndexedProductPriceChips })
@MapGetters(['productRecommendationMap', 'boostedProductSet', 'optimizerLanguage', 'indexedProductPrices'])
@MapActions(['setProductPriceRecommendationsConfirmed'])
export default class ProductPriceCard extends Vue {
  @Prop({ type: ProductRecommendation, required: true }) productRecommendation;

  @Prop({ type: Object, required: true }) saleFormatsByGuid;

  @Prop({ type: Boolean, default: false }) isConfirmationDisabled;

  @Prop({ type: String, default: '' }) group;

  @Prop({ type: Boolean, default: false }) showIndexedPrices;

  collapsed = false;

  arePricesValid = true;

  productName = ''

  get chipsWidth() {
    return 120;
  }

  get chipCssClasses() {
    return 'd-flex flex-row align-center justify-center flex-wrap flex-md-nowrap mt-1';
  }

  get cardCss() {
    let css = 'd-flex flex-wrap flex-md-nowrap flex-column flex-md-row justify-md-space-between pa-2 pt-2 py-md-1';
    if (this.hasSubordinateRecommendations) {
      css = 'd-flex flex-wrap flex-md-nowrap flex-column justify-md-space-between pa-2 pt-2 py-md-1';
    }
    return css;
  }

  get nameCss() {
    let css = 'font-weight-bold';
    if (this.hasSubordinateRecommendations) {
      css = 'font-weight-bold clickable';
      if (!this.arePricesValid) css += ' red--text';
    }
    return css;
  }

  get hasSubordinateRecommendations() {
    return this.productRecommendation.subordinate_products.length > 0;
  }

  get hasProductBoost() {
    return this.productRecommendation.allProductGuids.some((productGuid) => this.boostedProductSet.has(productGuid));
  }

  get hasPriceChanges() {
    const pricesRecommendations = this.productRecommendation.recommendations_by_demand;

    return Object.entries(pricesRecommendations).some(([level, levelData]) => this.hasPriceChange(levelData));
  }

  hasPriceChange(demandLevel) {
    return Object.values(demandLevel).some((idData) => Object.values(idData).some((itemData) => itemData.price !== itemData.previous_price));
  }

  get recommendations() {
    let recommendations = this.productRecommendation.subordinate_products.map((subordinateGuid) => this.productRecommendationMap.get(subordinateGuid));
    recommendations = [this.productRecommendation, ...recommendations];
    recommendations = recommendations.map((productRecommendation) => ({
      productRecommendation,
      saleFormatTitle: this.saleFormatsByGuid[productRecommendation.master_sale_format]
    }));
    return recommendations;
  }

  get generalConfirmedValue() {
    const areAllConfirmed = this.recommendations.every((recommendation) => recommendation.productRecommendation.confirmed);
    if (areAllConfirmed) return true;
    const areAllUnconfirmed = this.recommendations.every((recommendation) => !recommendation.productRecommendation.confirmed);
    if (areAllUnconfirmed) return false;
    return null;
  }

  get isConfirmationIndeterminate() {
    return this.generalConfirmedValue === null;
  }

  get collapseIcon() {
    return this.collapsed ? 'mdi-chevron-up' : 'mdi-chevron-down';
  }

  get showSubordinateProducts() {
    return this.hasSubordinateRecommendations && !this.collapsed;
  }

  async onConfirm(productRecommendation) {
    const productRecommendationGuids = [productRecommendation.guid];
    const confirmed = !productRecommendation.confirmed;
    await this.setProductPriceRecommendationsConfirmed({ productRecommendationGuids, confirmed });
    this.$emit('confirmation-changed');
  }

  async onConfirmAll() {
    const productRecommendationGuids = this.recommendations.map(({ productRecommendation }) => productRecommendation.guid);
    const confirmed = this.generalConfirmedValue === null ? false : !this.generalConfirmedValue;
    await this.setProductPriceRecommendationsConfirmed({ productRecommendationGuids, confirmed });
    this.$emit('confirmation-changed');
  }

  onCollapse() {
    this.collapsed = !this.collapsed;
  }

  onPricesValid(isValid) {
    this.arePricesValid = isValid;
  }

  @Watch('optimizerLanguage', { immediate: true })
  onOptimizerLanguage(newLanguage) {
    const newText = this.productRecommendation[`name${newLanguage.charAt(0).toUpperCase() + newLanguage.slice(1)}`];
    if (newText !== '') {
      this.productName = newText;
    }
  }

  getIndexedProductPrices(productGuid) {
    if (!this.showIndexedPrices) return null;
    return this.indexedProductPrices.find((productPrice) => productPrice.guid === productGuid) || null;
  }
}
</script>
<style>
.checkbox {
  margin-right: -10px;
}
</style>

<style scoped lang="scss">
.rocket-boost {
  position: absolute;
  top: 2px;
  left: 2px;
}

.collapse-button {
  margin-left: -5px;
}

.clickable {
  cursor: pointer;
}

.chip-ellipsis {
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  color: black;
}

.price-change-card {
    background-color: #c4d2eb;
}

</style>
